import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { EnvelopeSimple, Lock, Eye, EyeSlash } from "@phosphor-icons/react";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(email, password); }
    catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : Array.isArray(d) ? d.map(e=>e.msg).join(" ") : "Errore di accesso");
    } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col md:flex-row md:h-screen min-h-screen" data-testid="login-page">
      <div className="w-full md:w-[55%] relative overflow-hidden bg-gradient-to-br from-[#0D1F3C] via-[#142B5A] to-[#1B4FD8]">
        <div className="relative z-10 flex flex-col justify-between gap-10 md:gap-0 md:h-full p-8 sm:p-12">
          <h1 className="text-white text-3xl font-bold tracking-tight">SELLY<span className="text-[#56A3F5]">®</span></h1>
          <div className="max-w-2xl">
            <h2 className="text-white text-2xl sm:text-3xl md:text-4xl xl:text-5xl font-bold leading-[1.15] tracking-tight mb-5">
              <span className="block">Selezioni meglio.</span>
              <span className="block md:whitespace-nowrap">Crei team che funzionano.</span>
              <span className="block">Trattieni chi conta.</span>
            </h2>
            <p className="text-white/75 text-base sm:text-lg max-w-lg leading-relaxed">
              <span className="block">Analisi comportamentali</span>
              <span className="block">Simulazioni di compatibilità Team</span>
              <span className="block">Piani di sviluppo e onboarding personalizzati.</span>
              <span className="block mt-4">L'HR finalmente data-driven dalla scelta alla retention.</span>
            </p>
          </div>
          <p className="text-white/30 text-xs">www.selly-hr.it</p>
        </div>
      </div>
      <div className="w-full md:w-[45%] flex items-center justify-center bg-white px-6 sm:px-16 py-10 md:py-0">
        <div className="w-full max-w-[380px]">
          <h2 className="text-2xl font-semibold text-[#0D1F3C] mb-1">Accedi</h2>
          <p className="text-sm text-[#0D1F3C]/50 mb-8">Inserisci le tue credenziali per continuare</p>
          {error && <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4" data-testid="login-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label className="text-[#0D1F3C]/70 text-sm mb-1.5 block">Email</Label>
              <div className="relative">
                <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1F3C]/30" />
                <Input data-testid="login-email" type="email" placeholder="nome@azienda.it" value={email} onChange={e=>setEmail(e.target.value)}
                  className="pl-10 h-11 border-[#0D1F3C]/15 focus:border-[#1B4FD8] focus:ring-[#1B4FD8]" required />
              </div>
            </div>
            <div>
              <Label className="text-[#0D1F3C]/70 text-sm mb-1.5 block">Password</Label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1F3C]/30" />
                <Input data-testid="login-password" type={showPw?"text":"password"} placeholder="La tua password" value={password} onChange={e=>setPassword(e.target.value)}
                  className="pl-10 pr-10 h-11 border-[#0D1F3C]/15 focus:border-[#1B4FD8] focus:ring-[#1B4FD8]" required />
                <button type="button" onClick={()=>setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0D1F3C]/30 hover:text-[#0D1F3C]/60">
                  {showPw ? <EyeSlash size={18}/> : <Eye size={18}/>}
                </button>
              </div>
            </div>
            <Button data-testid="login-submit-btn" type="submit" disabled={loading}
              className="w-full h-11 bg-[#1B4FD8] hover:bg-[#0D1F3C] text-white font-medium transition-all duration-200">
              {loading ? "Accesso in corso..." : "Accedi"}
            </Button>
          </form>
          {/* B82 P1.1: bottone "Accedi con Google" rimosso — creava account
              duplicati per gli utenti registrati con email+password. Verra'
              reintrodotto solo dopo aver implementato l'aggancio per email. */}
          <p className="text-center text-sm text-gray-600 mt-6">
            Nuovo su Selly? <Link to="/register" className="font-semibold text-blue-600 hover:underline" data-testid="signup-cta-link">Provala gratis 14 giorni →</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
